import { Logger } from '@/utils/logger';
import type { DosageUnit } from './constants';
import { convertDosageUnit } from './convertDosageUnit';
import { parseMaxDailyDose } from './parseMaxDailyDose';

const logger = new Logger('extractMaxDailyDose');

/**
 * Where dosage text came from: product data, or text a prescriber may have typed (the previous prescription on a
 * reissue, or a pad item's dosage)
 */
export type DosageOrigin = { source: 'product' | 'reissue' | 'pad'; productId?: number };

/**
 * The parsed amount in the unit the pad item displays, so "Maximum daily dose 500mg" prefills 0.5 against a "g"
 * adornment. Undefined when the unit cannot be converted (mL against g), since there is no correct figure to prefill.
 */
const toDisplayedUnit = (amount: number, parsedUnit: DosageUnit, displayedUnit: DosageUnit | undefined) =>
  displayedUnit === undefined ? amount : convertDosageUnit(amount, parsedUnit, displayedUnit);

/** Whitespace up to and including its last line break, and the indentation after it */
const splitAtLastLineBreak = (gap: string) => {
  const lineStart = gap.lastIndexOf('\n') + 1;
  return [gap.slice(0, lineStart), gap.slice(lineStart)];
};

/**
 * Whitespace to join the text either side of the phrase with: a space, or where the phrase had a line of its own, the
 * line breaks before it and the next line's indentation as typed (blank lines and CRLF included)
 */
const getSeam = (before: string, after: string) => {
  const [breaksBefore, indentBefore] = splitAtLastLineBreak(before.slice(before.trimEnd().length));
  const [breaksAfter, indentAfter] = splitAtLastLineBreak(after.slice(0, after.length - after.trimStart().length));
  if (!breaksBefore && !breaksAfter) {
    return ' ';
  }
  return (breaksBefore || breaksAfter) + (breaksAfter ? indentAfter : indentBefore);
};

/**
 * Splits the maximum daily dose out of dosage text: the number in `unit`, and the text without it. A dose that cannot
 * be stated in `unit` stays in the text, with no number, so the limit is never restated in another unit. That is logged
 * as critical for product data, so it can be fixed, and as a warning for text a prescriber may have typed.
 */
export const extractMaxDailyDose = (dosage: string, unit?: DosageUnit, origin?: DosageOrigin) => {
  const match = parseMaxDailyDose(dosage);
  if (!match) {
    return { dosage, maxDailyDose: undefined };
  }
  const maxDailyDose = toDisplayedUnit(match.amount, match.unit, unit);
  if (maxDailyDose === undefined) {
    const level = origin?.source === 'product' ? 'critical' : 'warn';
    logger[level]('Maximum daily dose is in a unit the formulation cannot be converted to', {
      ...origin,
      amount: match.amount,
      parsedUnit: match.unit,
      unit,
    });
    return { dosage, maxDailyDose: undefined };
  }
  const [start, end] = match.position;
  const before = dosage.slice(0, start);
  const after = dosage.slice(end);
  // Joined only at the seam the phrase leaves, so the rest of the text keeps its whitespace. A phrase at the end can
  // leave the "; " separator behind ("...controlled;"); a typed "," before it stays
  const text = after.trim()
    ? [before.trimEnd(), after.trimStart()].filter(Boolean).join(getSeam(before, after))
    : before.trimEnd().replace(/;$/, '');
  return { dosage: text.trim(), maxDailyDose };
};
